"use client"

import { useMemo, useState } from "react"
import { motion, useMotionValue, useTransform, PanInfo } from "framer-motion"
import type { DailyEntry } from "@/lib/db/schema"
import { getZoneById } from "@/lib/zones"
import {
  getFocusedWeek,
  getCzechWeekdayShort,
  formatDayNumber,
  isTodayDate,
  groupEntriesByDay,
  calculateDailyAverage,
  averageToZoneId,
  toLocalDateString,
  calculateCurrentStreak,
} from "@/lib/date-utils"
import { getContrastColor, getMutedContrastColor } from "@/lib/color-utils"
import { cn } from "@/lib/utils"
import { addWeeks, subWeeks, subDays } from "date-fns"

interface MobileWeekViewProps {
  entries: DailyEntry[]
  centerDate?: Date
  onDayClick?: (date: Date) => void
  onWeekChange?: (newDate: Date) => void
}

/**
 * Check if a date is part of the current active streak
 */
function isInCurrentStreak(date: Date, entries: DailyEntry[]): boolean {
  if (entries.length === 0) return false

  const entriesByDate = groupEntriesByDay(entries)
  const dateStr = toLocalDateString(date)

  // Must have an entry on this date
  if (!entriesByDate.has(dateStr)) return false

  const today = toLocalDateString(new Date())
  const todayDate = new Date()

  // Check if there's a continuous streak from this date to today (or yesterday)
  let checkDate = todayDate

  // If no entry today, start from yesterday
  if (!entriesByDate.has(today)) {
    checkDate = subDays(todayDate, 1)
  }

  // Walk backwards from today/yesterday to see if this date is in the streak
  while (entriesByDate.has(toLocalDateString(checkDate))) {
    if (toLocalDateString(checkDate) === dateStr) {
      return true
    }
    checkDate = subDays(checkDate, 1)
  }

  return false
}

export function MobileWeekView({
  entries,
  centerDate = new Date(),
  onDayClick,
  onWeekChange,
}: MobileWeekViewProps) {
  const [dragDirection, setDragDirection] = useState<"left" | "right" | null>(null)

  // Get 7 days centered on current date (3 before + today + 3 after)
  const weekDays = useMemo(() => getFocusedWeek(centerDate), [centerDate])

  // Group entries by date for efficient lookups
  const entriesByDate = useMemo(() => groupEntriesByDay(entries), [entries])

  // Motion values for drag gestures
  const x = useMotionValue(0)
  const opacity = useTransform(x, [-200, 0, 200], [0.5, 1, 0.5])

  /**
   * Handle drag end - navigate to previous/next week based on swipe direction
   */
  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50 // Minimum drag distance to trigger week change
    const velocity = info.velocity.x

    // Determine if we should change week based on drag distance and velocity
    if (Math.abs(info.offset.x) > threshold || Math.abs(velocity) > 500) {
      if (info.offset.x > 0) {
        // Swiped right - go to previous week
        setDragDirection("right")
        const newDate = subWeeks(centerDate, 1)
        onWeekChange?.(newDate)
      } else {
        // Swiped left - go to next week
        setDragDirection("left")
        const newDate = addWeeks(centerDate, 1)
        onWeekChange?.(newDate)
      }
    }

    // Reset drag direction after animation
    setTimeout(() => setDragDirection(null), 300)
  }

  return (
    <motion.div
      className="w-full space-y-3"
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      style={{ x, opacity }}
      animate={{
        x: 0,
        opacity: 1,
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
      }}
    >
      {/* Week days grid */}
      <div className="grid grid-cols-7 gap-2 sm:gap-2">
        {weekDays.map((date, index) => {
          const dateStr = toLocalDateString(date)
          const dayEntries = entriesByDate.get(dateStr) || []
          const isToday = isTodayDate(date)
          const entryCount = dayEntries.length

          // Calculate average zone for background color
          const averageZone = calculateDailyAverage(dayEntries)
          const averageZoneId = averageToZoneId(averageZone)
          const zone = averageZoneId ? getZoneById(averageZoneId) : null

          // Check if this day is part of current streak
          const inStreak = isInCurrentStreak(date, entries)
          const nextDayInStreak = index < weekDays.length - 1 && isInCurrentStreak(weekDays[index + 1], entries)

          return (
            <motion.div
              key={dateStr}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="flex flex-col items-center relative"
            >
              {/* Weekday label */}
              <div className="text-[10px] sm:text-xs text-muted-foreground font-medium mb-1">
                {getCzechWeekdayShort(date)}
              </div>

              {/* Streak connecting line */}
              {inStreak && nextDayInStreak && (
                <div
                  className="absolute top-[32px] left-[calc(50%+24px)] sm:left-[calc(50%+28px)] w-[calc(100%-48px)] sm:w-[calc(100%-56px)] h-1"
                  style={{
                    background: "linear-gradient(90deg, rgba(249, 115, 22, 0.7) 0%, rgba(251, 191, 36, 0.7) 100%)",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Day cell */}
              <button
                onClick={() => onDayClick?.(date)}
                className={cn(
                  "relative w-11 h-11 sm:w-14 sm:h-14 rounded-xl",
                  "flex flex-col items-center justify-center",
                  "transition-all duration-200",
                  "hover:scale-110 active:scale-95",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                  isToday && "ring-2 ring-primary ring-offset-2",
                  entryCount === 0 && "bg-muted/30 hover:bg-muted/50",
                  entryCount > 0 && !inStreak && "shadow-md hover:shadow-lg",
                  // Streak highlighting - more prominent with ring and glow
                  inStreak && "ring-[3px] ring-orange-500/90 shadow-xl"
                )}
                style={{
                  backgroundColor: zone?.color
                    ? zone.color.replace(/oklch\(([^)]+)\)/, 'oklch($1 / 0.85)')
                    : undefined,
                  opacity: entryCount === 0 ? 0.5 : 1,
                  boxShadow: inStreak
                    ? "0 0 20px rgba(249, 115, 22, 0.4), 0 6px 15px rgba(249, 115, 22, 0.2)"
                    : undefined,
                }}
                aria-label={`${formatDayNumber(date)}. ${getCzechWeekdayShort(date)}, ${entryCount} ${
                  entryCount === 1 ? "záznam" : "záznamy"
                }${inStreak ? ", součást série" : ""}`}
              >
                {/* Day number */}
                <span
                  className={cn(
                    "text-sm sm:text-base font-semibold",
                    !zone && "text-muted-foreground",
                    isToday && "font-bold"
                  )}
                  style={{
                    color: zone?.color ? getContrastColor(zone.color) : undefined
                  }}
                >
                  {formatDayNumber(date)}
                </span>

                {/* Entry count dots */}
                {entryCount > 0 && (
                  <div className="flex gap-0.5 mt-1 items-center justify-center">
                    {Array.from({ length: Math.min(entryCount, 4) }).map((_, i) => (
                      <div
                        key={i}
                        className="w-1 h-1 rounded-full"
                        style={{
                          backgroundColor: zone?.color
                            ? getMutedContrastColor(zone.color)
                            : undefined
                        }}
                      />
                    ))}
                  </div>
                )}
              </button>
            </motion.div>
          )
        })}
      </div>
    </motion.div>
  )
}
